// src/utils/colorConversions.js
// Funções de conversão entre espaços de cor

/**
 * Converte XYZ para valores RGB numéricos [0-1]
 */
export function xyzToRgbNum(X, Y, Z, applyGamma = false) {
    let r = 3.2406 * X + (-1.5372) * Y + (-0.4986) * Z;
    let g = -0.9689 * X + 1.8758 * Y + 0.0415 * Z;
    let b = 0.0557 * X + (-0.2040) * Y + 1.0570 * Z;

    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);

    const maxc = Math.max(r, g, b, 1);
    if (maxc > 1) { r /= maxc; g /= maxc; b /= maxc; }

    if (applyGamma) {
        const oetf = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
        r = oetf(r);
        g = oetf(g);
        b = oetf(b);
    }

    return [Math.max(0, Math.min(1, r)), Math.max(0, Math.min(1, g)), Math.max(0, Math.min(1, b))];
}

/**
 * Converte xyY para RGB numéricos [0-255]
 */
export function xyYtoRgbNum(x, y, Y = 1.0, applyGamma = false) {
    if (y <= 1e-8) return [0, 0, 0];

    const X = (x * Y) / y;
    const Z = ((1 - x - y) * Y) / y;

    const [r, g, b] = xyzToRgbNum(X, Y, Z, applyGamma);
    return [r * 255, g * 255, b * 255];
}

/**
 * Interpola valores escalares de um array para um tamanho alvo
 */
export function interpolateScalarValues(values, targetLength) {
    if (!Array.isArray(values) || values.length === 0) return [];
    if (values.length === targetLength) return [...values];
    const result = [];
    const src = values.length;
    for (let i = 0; i < targetLength; i++) {
        if (targetLength === 1) { result.push(values[0]); continue; }
        const pos = (i / (targetLength - 1)) * (src - 1);
        const lo = Math.floor(pos);
        const hi = Math.min(Math.ceil(pos), src - 1);
        if (lo === hi) { result.push(values[lo]); continue; }
        const frac = pos - lo;
        result.push(values[lo] + (values[hi] - values[lo]) * frac);
    }
    return result;
}

/**
 * Interpola cores subamostradas de volta ao tamanho original (RGB linear)
 */
export function interpolateLinearRgb(rgbArray, targetLength) {
    if (!Array.isArray(rgbArray) || rgbArray.length === 0) {
        console.warn('[interpolateLinearRgb] Array RGB vazio ou inválido');
        return [];
    }

    if (rgbArray.length === targetLength) {
        return rgbArray.map((rgb) => ({ r: rgb.r, g: rgb.g, b: rgb.b }));
    }

    const result = [];
    const sourceLength = rgbArray.length;

    for (let i = 0; i < targetLength; i += 1) {
        if (targetLength === 1) {
            result.push({ ...rgbArray[0] });
            continue;
        }

        const position = (i / (targetLength - 1)) * (sourceLength - 1);
        const lowerIdx = Math.floor(position);
        const upperIdx = Math.min(Math.ceil(position), sourceLength - 1);

        if (lowerIdx === upperIdx) {
            result.push({ ...rgbArray[lowerIdx] });
            continue;
        }

        const fraction = position - lowerIdx;
        const lower = rgbArray[lowerIdx];
        const upper = rgbArray[upperIdx];

        if (!lower || !upper) {
            console.warn('[interpolateLinearRgb] Valores indefinidos durante interpolação', { lowerIdx, upperIdx });
            result.push({ r: 0, g: 0, b: 0 });
            continue;
        }

        result.push({
            r: lower.r + (upper.r - lower.r) * fraction,
            g: lower.g + (upper.g - lower.g) * fraction,
            b: lower.b + (upper.b - lower.b) * fraction,
        });
    }

    return result;
}

/**
 * Converte array de objetos sRGB (0-1, já com gamma/OETF) para strings 'rgb(r,g,b)' (0-255)
 */
export function convertSrgbArrayToStrings(srgbArray) {
    return srgbArray.map(c => {
        const r = Math.round(Math.max(0, Math.min(1, c.r)) * 255);
        const g = Math.round(Math.max(0, Math.min(1, c.g)) * 255);
        const b = Math.round(Math.max(0, Math.min(1, c.b)) * 255);
        return `rgb(${r},${g},${b})`;
    });
}

/**
 * Converte um único objeto sRGB (0-1, já com gamma/OETF) para string 'rgb(r,g,b)'
 */
export function srgbToRgbString(c) {
    const r = Math.round(Math.max(0, Math.min(1, c.r)) * 255);
    const g = Math.round(Math.max(0, Math.min(1, c.g)) * 255);
    const b = Math.round(Math.max(0, Math.min(1, c.b)) * 255);
    return `rgb(${r},${g},${b})`;
}

/**
 * Converte LAB para RGB string
 */
export function labToRgb(l, a, b, applyGamma = false) {
    let y = (l + 16) / 116;
    let x = a / 500 + y;
    let z = y - b / 200;

    x = 0.95047 * ((x * x * x > 0.008856) ? x * x * x : (x - 16 / 116) / 7.787);
    y = 1.00000 * ((y * y * y > 0.008856) ? y * y * y : (y - 16 / 116) / 7.787);
    z = 1.08883 * ((z * z * z > 0.008856) ? z * z * z : (z - 16 / 116) / 7.787);

    let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
    let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
    let bVal = x * 0.0557 + y * -0.2040 + z * 1.0570;

    r = Math.max(0, r);
    g = Math.max(0, g);
    bVal = Math.max(0, bVal);
    const maxc = Math.max(r, g, bVal, 1);
    if (maxc > 1) { r /= maxc; g /= maxc; bVal /= maxc; }

    if (applyGamma) {
        r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
        g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
        bVal = bVal > 0.0031308 ? 1.055 * Math.pow(bVal, 1 / 2.4) - 0.055 : 12.92 * bVal;
    }

    r = Math.max(0, Math.min(1, r)) * 255;
    g = Math.max(0, Math.min(1, g)) * 255;
    bVal = Math.max(0, Math.min(1, bVal)) * 255;

    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(bVal)})`;
}

/**
 * Converte XYZ para RGB string
 */
export function xyzToRgb(x, y, z, applyGamma = false) {
    let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
    let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
    let b = x * 0.0557 + y * -0.2040 + z * 1.0570;

    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);

    const maxc = Math.max(r, g, b, 1);
    if (maxc > 1) { r /= maxc; g /= maxc; b /= maxc; }

    if (applyGamma) {
        r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
        g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
        b = b > 0.0031308 ? 1.055 * Math.pow(b, 1 / 2.4) - 0.055 : 12.92 * b;
    }

    r = Math.max(0, Math.min(1, r)) * 255;
    g = Math.max(0, Math.min(1, g)) * 255;
    b = Math.max(0, Math.min(1, b)) * 255;

    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

/**
 * Converte xyY para RGB string (via XYZ)
 */
export function xyYToRgb(xVal, yVal, YVal, applyGamma = false) {
    if (yVal === 0 || yVal < 0.0001) {
        return `rgb(0,0,0)`;
    }

    const X = (xVal * YVal) / yVal;
    const Y = YVal;
    const Z = ((1 - xVal - yVal) * YVal) / yVal;

    return xyzToRgb(X, Y, Z, applyGamma);
}

/**
 * Converte HSV para RGB string
 */
export function hsvToRgb(h, s, v, applyGamma = false) {
    h = h / 360;
    s = s / 100;
    v = v / 100;

    let r, g, b;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);

    switch (i % 6) {
        case 0: r = v; g = t; b = p; break;
        case 1: r = q; g = v; b = p; break;
        case 2: r = p; g = v; b = t; break;
        case 3: r = p; g = q; b = v; break;
        case 4: r = t; g = p; b = v; break;
        case 5: r = v; g = p; b = q; break;
        default: r = 0; g = 0; b = 0;
    }

    if (applyGamma) {
        r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
        g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
        b = b > 0.0031308 ? 1.055 * Math.pow(b, 1 / 2.4) - 0.055 : 12.92 * b;
    }

    r = Math.max(0, Math.min(1, r)) * 255;
    g = Math.max(0, Math.min(1, g)) * 255;
    b = Math.max(0, Math.min(1, b)) * 255;

    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

/**
 * Converte HSB para RGB string (idêntico a HSV)
 */
export function hsbToRgb(h, s, b, applyGamma = false) {
    return hsvToRgb(h, s, b, applyGamma);
}

/**
 * Converte CMYK para RGB string
 */
export function cmykToRgb(c, m, y, k, applyGamma = false) {
    let r = (1 - c) * (1 - k);
    let g = (1 - m) * (1 - k);
    let b = (1 - y) * (1 - k);

    if (applyGamma) {
        r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
        g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
        b = b > 0.0031308 ? 1.055 * Math.pow(b, 1 / 2.4) - 0.055 : 12.92 * b;
    }

    r = Math.max(0, Math.min(1, r)) * 255;
    g = Math.max(0, Math.min(1, g)) * 255;
    b = Math.max(0, Math.min(1, b)) * 255;

    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

/**
 * Converte xyY para RGB inteiros [0-255] (usado no canvas do CIE)
 */
export function xyYToRgbInts(x, y, Y = 1.0, applyOETFFlag = false) {
    if (!y || y <= 1e-8) return [0, 0, 0];

    const X = (x * Y) / y;
    const Yv = Y;
    const Z = ((1 - x - y) * Y) / y;

    let r = 3.2406 * X + (-1.5372) * Yv + (-0.4986) * Z;
    let g = -0.9689 * X + 1.8758 * Yv + 0.0415 * Z;
    let b = 0.0557 * X + (-0.2040) * Yv + 1.0570 * Z;

    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);

    const maxc = Math.max(r, g, b, 1);
    if (maxc > 1) { r /= maxc; g /= maxc; b /= maxc; }

    if (applyOETFFlag) {
        const oetf = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
        r = oetf(r);
        g = oetf(g);
        b = oetf(b);
    }

    const clampToByte = (v) => Math.round(Math.max(0, Math.min(1, v)) * 255);
    return [clampToByte(r), clampToByte(g), clampToByte(b)];
}

/**
 * Estima o comprimento de onda dominante (nm) a partir de coordenadas de cromaticidade CIE xy.
 * Usa intersecção da reta do iluminante D65 → (x,y) com o locus espectral.
 * Retorna null para cores não-espectrais (púrpuras) ou coordenadas inválidas.
 */
export function dominantWavelength(x, y) {
    if (x == null || y == null || (x === 0 && y === 0)) return null;

    // Iluminante D65
    const xn = 0.3127, yn = 0.3290;
    const dx = x - xn, dy = y - yn;
    const angle = Math.atan2(dy, dx);

    // Locus espectral CIE 1931 2° (pontos selecionados 380-700nm)
    const locus = [
        [380,0.1741,0.0050],[390,0.1740,0.0050],[400,0.1734,0.0048],[410,0.1714,0.0051],
        [420,0.1644,0.0109],[430,0.1566,0.0177],[440,0.1440,0.0297],[450,0.1241,0.0578],
        [460,0.0913,0.1327],[470,0.0454,0.2950],[480,0.0082,0.5384],[490,0.0139,0.7502],
        [500,0.0743,0.8338],[510,0.1547,0.8059],[520,0.2296,0.7543],[530,0.2950,0.6923],
        [540,0.3616,0.6245],[550,0.4294,0.5547],[560,0.4929,0.4866],[570,0.5498,0.4242],
        [580,0.5983,0.3690],[590,0.6270,0.3290],[600,0.6482,0.3101],[610,0.6658,0.2949],
        [620,0.6915,0.3083],[630,0.7079,0.2920],[640,0.7190,0.2809],[650,0.7260,0.2740],
        [660,0.7300,0.2700],[670,0.7320,0.2680],[680,0.7334,0.2666],[690,0.7344,0.2656],
        [700,0.7347,0.2653],
    ];

    let bestNm = null;
    let bestDist = Infinity;

    for (let i = 0; i < locus.length - 1; i++) {
        const [nm1, x1, y1] = locus[i];
        const [nm2, x2, y2] = locus[i + 1];

        // Intersecção da reta do iluminante com o segmento do locus
        const ex = x2 - x1, ey = y2 - y1;
        const denom = dx * ey - dy * ex;
        if (Math.abs(denom) < 1e-12) continue;

        const t = ((x1 - xn) * ey - (y1 - yn) * ex) / denom;
        const u = ((x1 - xn) * dy - (y1 - yn) * dx) / denom;

        if (t > 0 && u >= 0 && u <= 1) {
            const nm = nm1 + u * (nm2 - nm1);
            const ix = xn + t * dx;
            const iy = yn + t * dy;
            const dist = (ix - x) * (ix - x) + (iy - y) * (iy - y);
            if (dist < bestDist) {
                bestDist = dist;
                bestNm = Math.round(nm);
            }
        }
    }

    return bestNm;
}
